<template>
  <div class="welcome-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-bg"></div>
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <div class="hero-badge">
          <div class="hero-badge-logo-wrap">
            <img :src="imageLogoOnDarkRmBg" alt="CertifAI" class="hero-badge-logo" />
            <div class="hero-badge-glow"></div>
          </div>
          <span>Certification Compliance Platform</span>
        </div>
        <h1 class="hero-title">{{ $t('certifai-welcome.title') }}</h1>
        <p class="hero-subtitle">
          Streamline your system certification journey with an integrated framework for conformity
          assessment, risk management, threat modeling and regulatory compliance.
        </p>
        <div class="hero-actions">
          <v-btn class="get-started-btn" large rounded dark @click="getStarted">
            <v-icon left>mdi-rocket-launch-outline</v-icon>
            {{ $t('certifai-welcome.get-started') }}
          </v-btn>
          <a href="https://certifai.info/" target="_blank" class="learn-more-link">
            <v-btn class="learn-more-btn" large rounded outlined dark>
              <v-icon left>mdi-open-in-new</v-icon>
              Learn More
            </v-btn>
          </a>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section">
      <div class="section-header">
        <div class="section-icon-wrap">
          <v-icon color="white" size="22">mdi-view-dashboard-outline</v-icon>
        </div>
        <h2 class="section-title">How It Works</h2>
      </div>
      <p class="section-description">
        CertifAI guides you through every step of the certification compliance process — from
        documentation and evidence collection to conformity assessment and reporting.
      </p>

      <div class="features-grid">
        <!-- Feature Card 1 -->
        <div class="feature-card feature-card--blue">
          <div class="feature-step">1</div>
          <div class="feature-icon-wrap feature-icon--blue">
            <v-icon color="white" size="36">mdi-cloud-upload-outline</v-icon>
          </div>
          <h3 class="feature-title">
            {{ $t('certifai-welcome.welcome-steps.panel-one.title') }}
          </h3>
          <p class="feature-bio">
            {{ $t('certifai-welcome.welcome-steps.panel-one.bio') }}
          </p>
          <ul class="feature-bullets">
            <li>
              <v-icon color="#1976d2" size="18">mdi-check-circle</v-icon>
              {{ $t('certifai-welcome.welcome-steps.panel-one.bullet-one') }}
            </li>
            <li>
              <v-icon color="#1976d2" size="18">mdi-check-circle</v-icon>
              {{ $t('certifai-welcome.welcome-steps.panel-one.bullet-two') }}
            </li>
          </ul>
        </div>

        <!-- Arrow Connector -->
        <div class="arrow-connector">
          <v-icon color="#1976d2" size="28">mdi-chevron-right</v-icon>
        </div>

        <!-- Feature Card 2 -->
        <div class="feature-card feature-card--purple">
          <div class="feature-step feature-step--purple">2</div>
          <div class="feature-icon-wrap feature-icon--purple">
            <v-icon color="white" size="36">mdi-magnify</v-icon>
          </div>
          <h3 class="feature-title">
            {{ $t('certifai-welcome.welcome-steps.panel-two.title') }}
          </h3>
          <p class="feature-bio">
            {{ $t('certifai-welcome.welcome-steps.panel-two.bio') }}
          </p>
          <ul class="feature-bullets">
            <li>
              <v-icon color="#7b1fa2" size="18">mdi-check-circle</v-icon>
              {{ $t('certifai-welcome.welcome-steps.panel-two.bullet-one') }}
            </li>
            <li>
              <v-icon color="#7b1fa2" size="18">mdi-check-circle</v-icon>
              {{ $t('certifai-welcome.welcome-steps.panel-two.bullet-two') }}
            </li>
          </ul>
        </div>

        <!-- Arrow Connector -->
        <div class="arrow-connector">
          <v-icon color="#1976d2" size="28">mdi-chevron-right</v-icon>
        </div>

        <!-- Feature Card 3 -->
        <div class="feature-card feature-card--teal">
          <div class="feature-step feature-step--teal">3</div>
          <div class="feature-icon-wrap feature-icon--teal">
            <v-icon color="white" size="36">mdi-cog-outline</v-icon>
          </div>
          <h3 class="feature-title">
            {{ $t('certifai-welcome.welcome-steps.panel-three.title') }}
          </h3>
          <p class="feature-bio">
            {{ $t('certifai-welcome.welcome-steps.panel-three.bio') }}
          </p>
          <ul class="feature-bullets">
            <li>
              <v-icon color="#00838f" size="18">mdi-check-circle</v-icon>
              {{ $t('certifai-welcome.welcome-steps.panel-three.bullet-one') }}
            </li>
            <li>
              <v-icon color="#00838f" size="18">mdi-check-circle</v-icon>
              {{ $t('certifai-welcome.welcome-steps.panel-three.bullet-two') }}
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Highlights Row -->
    <section class="highlights-section">
      <div class="highlight-card highlight--blue">
        <div class="highlight-icon">
          <v-icon color="white" size="28">mdi-file-check-outline</v-icon>
        </div>
        <div class="highlight-info">
          <span class="highlight-label">Conformity Assessment</span>
          <span class="highlight-desc">CRA and other standards aligned checks</span>
        </div>
      </div>
      <div class="highlight-card highlight--purple">
        <div class="highlight-icon highlight-icon--purple">
          <v-icon color="white" size="28">mdi-spider-web</v-icon>
        </div>
        <div class="highlight-info">
          <span class="highlight-label">Threat Modeling / Risk Assessment</span>
          <span class="highlight-desc"
            >Use the threat modeling and risk assessment capabilities of Threat Spider tool</span
          >
        </div>
      </div>
      <div class="highlight-card highlight--teal">
        <div class="highlight-icon highlight-icon--teal">
          <v-icon color="white" size="28">mdi-package-variant-closed</v-icon>
        </div>
        <div class="highlight-info">
          <span class="highlight-label">SBOM Management</span>
          <span class="highlight-desc"
            >Software supply chain visibility using VimPact and SBOM visualization tools</span
          >
        </div>
      </div>
      <div class="highlight-card highlight--green">
        <div class="highlight-icon highlight-icon--green">
          <v-icon color="white" size="28">mdi-shield-lock-outline</v-icon>
        </div>
        <div class="highlight-info">
          <span class="highlight-label">Evidence Collection</span>
          <span class="highlight-desc">Structured audit-ready records of evidence</span>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="cta-card">
        <h2 class="cta-title">Ready to get started?</h2>
        <p class="cta-text">
          Join the CertifAI platform and take the first step toward trustworthy compliant systems.
        </p>
        <v-btn class="cta-btn" x-large rounded dark @click="getStarted">
          <v-icon left>mdi-arrow-right-circle-outline</v-icon>
          {{ $t('certifai-welcome.get-started') }}
        </v-btn>
      </div>
    </section>
  </div>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api';
import store from '@/store/store';
import router from '@/router';
import imageLogoOnDarkRmBg from '@/assets/logo-on-dark-removebg.png';

export default defineComponent({
  name: 'Welcome',
  components: [imageLogoOnDarkRmBg],
  setup() {
    const loginUrl = process.env.VUE_APP_LOGINPATH;
    const registerUrl = process.env.VUE_APP_REGISTERPATH;
    const isAuthenticated = computed(() => store.getters.fullName);
    const isRegistrationOpen = process.env.VUE_APP_REGISTRATION_OPEN;

    const getStarted = () => {
      if (isAuthenticated.value === 'undefined') {
        router.push('/dashboard');
      } else {
        var destUrl = registerUrl;
        if (isRegistrationOpen == 'false') destUrl = loginUrl;
        document.location.href = destUrl;
      }
    };

    return { getStarted, imageLogoOnDarkRmBg };
  },
});
</script>

<style lang="scss" scoped>
/* =============================================
   Welcome Page — matches CertifAI design language
   ============================================= */

.welcome-page {
  min-height: 100vh;
  background: #f8f9fa;
  overflow-x: hidden;
}

/* ---------- Hero Section ---------- */
.hero-section {
  position: relative;
  min-height: 520px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 24px 60px;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #1a237e 0%, #283593 40%, #303f9f 70%, #0097a7 100%);
  z-index: 0;
}

.hero-bg::before {
  content: '';
  position: absolute;
  top: -40%;
  right: -20%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(0, 151, 167, 0.25) 0%, transparent 70%);
  border-radius: 50%;
}

.hero-bg::after {
  content: '';
  position: absolute;
  bottom: -30%;
  left: -10%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(88, 30, 92, 0.2) 0%, transparent 70%);
  border-radius: 50%;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: url('/landingpage.jpg') center / cover no-repeat;
  opacity: 0.08;
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 740px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 8px 22px 8px 8px;
  background: linear-gradient(
    135deg,
    #00acc1 0%,
    #00838f 100%
  ); //linear-gradient(20deg, #7d60ff 0%, #5a09b6 100%); // rgba(215, 55, 255, 0.12);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 28px;
}

/* Logo inside badge */
.hero-badge-logo-wrap {
  position: relative;
  width: 164px;
  height: 164px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-badge-logo {
  width: 164px;
  height: 164px;
  object-fit: contain;
  border-radius: 50%;
  position: relative;
  z-index: 2;
  animation: logo-float 4s ease-in-out infinite, logo-flash 5s ease-in-out infinite;
  /* White-tinted glow keeps blue letters in the logo fully visible */
  filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.753));
}

.hero-badge-glow {
  position: absolute;
  inset: -5px;
  border-radius: 50%;
  background: radial-gradient(circle, rgb(37, 255, 110) 0%, transparent 70%);
  animation: glow-pulse 3s ease-in-out infinite;
  z-index: 1;
}

.hero-badge-glow::after {
  content: '';
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.25);
  animation: ring-spin 8s linear infinite;
}

.hero-title {
  font-size: 3rem;
  font-weight: 800;
  color: #ffffff;
  line-height: 1.15;
  margin-bottom: 20px;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);
}

.hero-subtitle {
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.7;
  max-width: 620px;
  margin: 0 auto 36px;
}

.hero-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.get-started-btn {
  background: linear-gradient(135deg, #00acc1 0%, #00838f 100%) !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  padding: 0 32px !important;
  box-shadow: 0 4px 20px rgba(0, 172, 193, 0.35) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.get-started-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(0, 172, 193, 0.45) !important;
}

.learn-more-btn {
  border-color: rgba(255, 255, 255, 0.5) !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
}

.learn-more-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.8) !important;
}

.learn-more-link {
  text-decoration: none;
}

/* ---------- Features Section ---------- */
.features-section {
  padding: 64px 24px 48px;
  max-width: 1100px;
  margin: 0 auto;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
}

.section-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #00405e 0%, #0097a7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-title {
  font-size: 1.65rem;
  font-weight: 700;
  color: #1a237e;
  margin: 0;
}

.section-description {
  font-size: 1rem;
  color: #616161;
  max-width: 1060px;
  line-height: 1.65;
  margin: 0 0 40px 54px;
}

.features-grid {
  display: flex;
  align-items: stretch;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

.arrow-connector {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-card {
  position: relative;
  flex: 1 1 260px;
  max-width: 320px;
  background: #ffffff;
  border-radius: 16px;
  padding: 40px 24px 28px;
  text-align: center;
  border: 1px solid #e0e0e0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.feature-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.12);
}

.feature-card--blue:hover {
  border-color: #1976d2;
  background: linear-gradient(180deg, rgba(25, 118, 210, 0.03) 0%, #ffffff 100%);
}

.feature-card--purple:hover {
  border-color: #7b1fa2;
  background: linear-gradient(180deg, rgba(123, 31, 162, 0.03) 0%, #ffffff 100%);
}

.feature-card--teal:hover {
  border-color: #00838f;
  background: linear-gradient(180deg, rgba(0, 131, 143, 0.03) 0%, #ffffff 100%);
}

.feature-step {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1976d2 0%, #64b5f6 100%);
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(25, 118, 210, 0.35);
}

.feature-step--purple {
  background: linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%);
  box-shadow: 0 3px 10px rgba(123, 31, 162, 0.35);
}

.feature-step--teal {
  background: linear-gradient(135deg, #00838f 0%, #00acc1 100%);
  box-shadow: 0 3px 10px rgba(0, 131, 143, 0.35);
}

.feature-icon-wrap {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
}

.feature-icon--blue {
  background: linear-gradient(135deg, #1976d2 0%, #64b5f6 100%);
  box-shadow: 0 6px 20px rgba(25, 118, 210, 0.3);
}

.feature-icon--purple {
  background: linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%);
  box-shadow: 0 6px 20px rgba(123, 31, 162, 0.3);
}

.feature-icon--teal {
  background: linear-gradient(135deg, #00838f 0%, #00acc1 100%);
  box-shadow: 0 6px 20px rgba(0, 131, 143, 0.3);
}

.feature-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #212121;
  margin-bottom: 8px;
}

.feature-bio {
  font-size: 0.9rem;
  color: #757575;
  line-height: 1.55;
  margin-bottom: 16px;
}

.feature-bullets {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.85rem;
    color: #424242;
    line-height: 1.5;
    margin-bottom: 8px;

    .v-icon {
      flex-shrink: 0;
      margin-top: 2px;
    }
  }
}

/* ---------- Highlights Row ---------- */
.highlights-section {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  padding: 0 24px 64px;
  max-width: 1100px;
  margin: 0 auto;
}

.highlight-card {
  flex: 1 1 220px;
  max-width: 260px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.highlight-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(5, 81, 247, 0.301);
}

.highlight-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1976d2 0%, #64b5f6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.highlight-icon--purple {
  background: linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%);
}

.highlight-icon--teal {
  background: linear-gradient(135deg, #00838f 0%, #00acc1 100%);
}

.highlight-icon--green {
  background: linear-gradient(135deg, #388e3c 0%, #81c784 100%);
}

.highlight-info {
  display: flex;
  flex-direction: column;
}

.highlight-label {
  font-size: 0.92rem;
  font-weight: 700;
  color: #212121;
}

.highlight-desc {
  font-size: 0.78rem;
  color: #070707be;
  margin-top: 2px;
}

/* ---------- CTA Section ---------- */
.cta-section {
  padding: 0 24px 80px;
  max-width: 800px;
  margin: 0 auto;
}

.cta-card {
  text-align: center;
  padding: 48px 32px;
  border-radius: 20px;
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #303f9f 100%);
  box-shadow: 0 8px 32px rgba(26, 35, 126, 0.25);
  position: relative;
  overflow: hidden;
}

.cta-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -30%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(0, 151, 167, 0.2) 0%, transparent 70%);
  border-radius: 50%;
}

.cta-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 12px;
  position: relative;
}

.cta-text {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.65;
  max-width: 480px;
  margin: 0 auto 28px;
  position: relative;
}

.cta-btn {
  background: linear-gradient(135deg, #00acc1 0%, #00838f 100%) !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 4px 20px rgba(0, 172, 193, 0.4) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative;
}

.cta-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(0, 172, 193, 0.5) !important;
}

/* ---------- Responsive ---------- */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .features-grid {
    flex-direction: column;
    align-items: center;
  }

  .arrow-connector {
    transform: rotate(90deg);
  }

  .feature-card {
    max-width: 100%;
  }

  .section-description {
    margin-left: 0;
  }

  .highlights-section {
    flex-direction: column;
    align-items: center;
  }

  .highlight-card {
    max-width: 100%;
  }
}
/* ---------- Hero Brand Row (logo + badge) ---------- */
.hero-brand {
  display: none;
}

@keyframes logo-float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
}

@keyframes logo-flash {
  0%,
  75%,
  100% {
    filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.5));
  }
  80% {
    /* Bright white burst — preserves all logo colors including blue */
    filter: drop-shadow(0 0 18px rgba(255, 255, 255, 1)) brightness(1.2);
  }
  88% {
    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.65));
  }
}

@keyframes glow-pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

@keyframes ring-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
